/**
 * Deep Mode Panel
 * ---------------
 * Top-of-page panel on /framework that turns the Therapy Library into the
 * second half of the AI conversation rather than a separate tool.
 *
 * Behavior:
 *   - If a conversation context was bridged from the homepage / dashboard
 *     chat (`navigatorDeepContext`), auto-runs the 3-phase Consciousness
 *     Engine on that text and renders the visualizations inline.
 *   - If no bridged context, shows a friendly invitation to start (or
 *     return to) the Reso chat first — no separate text input here.
 *
 * Why no input box: the goal is "same AI, going deeper" — the user must not
 * be asked to re-type what they already shared.
 */
import { useEffect, useMemo, useRef, useState, lazy, Suspense, useCallback } from 'react';
import { aiText } from '@/i18n/ai-conversation';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, Sparkles, ArrowRight, MessageCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  peekDeepModeContext,
  consumeDeepModeContext,
  composeDeepInput,
  setDeepModeResult,
  type DeepModeContext,
} from '@/components/dashboard/client/ai-navigator/navigatorDeepContext';

import EvidencePanel from '@/components/consciousness-demo/EvidencePanel';
import PhenomenonPanel from '@/components/consciousness-demo/PhenomenonPanel';
import StructuralRadar from '@/components/consciousness-demo/StructuralRadar';
import FalseVPanel from '@/components/consciousness-demo/FalseVPanel';
import HealingStagePanel from '@/components/consciousness-demo/HealingStagePanel';
import TherapySuggestions from '@/components/consciousness-demo/TherapySuggestions';
import NutrientPanel from '@/components/consciousness-demo/NutrientPanel';
import AIAdvicePanel from '@/components/consciousness-demo/AIAdvicePanel';
import PhaseProgressStepper from '@/components/consciousness-demo/PhaseProgressStepper';
import CausalChainsPanel from '@/components/consciousness-demo/CausalChainsPanel';

const ConsciousnessGraph3D = lazy(() => import('@/components/consciousness-demo/ConsciousnessGraph3D'));

function detectLanguage(text: string): 'zh' | 'en' {
  const cjkRegex = /[\u4e00-\u9fff\u3400-\u4dbf\uf900-\ufaff]/g;
  const cjkMatches = text.match(cjkRegex);
  const ratio = (cjkMatches?.length || 0) / Math.max(text.replace(/\s/g, '').length, 1);
  return ratio > 0.3 ? 'zh' : 'en';
}

interface Phase1 { evidence: any[]; phenomena: any[]; edges: any[] }
interface Phase2 {
  structural_scores: Record<string, number>;
  false_v: { smc: any; msf: any; ifr: any; k: any };
  edges: any[];
  healing_stage: { stage: number; stage_name: string; rationale: string };
}
interface Phase3 {
  therapy_suggestions: any[];
  matched_therapies?: any[];
  matched_nutrients?: any[];
  ai_advice?: any[];
}

const DeepModePanel: React.FC = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isZh = i18n.language?.startsWith('zh');
  const T = aiText(i18n.language);
  const [searchParams] = useSearchParams();
  // Explicit flag set by the "Go deeper" button — guarantees we render
  // SOMETHING (loading / context / fallback) even if sessionStorage was
  // already consumed by a prior mount (React StrictMode double-effect,
  // lazy chunk remount, etc.).
  const bridgeRequested = searchParams.get('deep') === '1';

  const [context, setContext] = useState<DeepModeContext | null>(null);
  const [contextMissing, setContextMissing] = useState(false);
  const [phase, setPhase] = useState<0 | 1 | 2 | 3>(0);
  const [loading, setLoading] = useState(false);
  const [phase1, setPhase1] = useState<Phase1 | null>(null);
  const [phase2, setPhase2] = useState<Phase2 | null>(null);
  const [phase3, setPhase3] = useState<Phase3 | null>(null);
  const [highlightedEvIds, setHighlightedEvIds] = useState<string[]>([]);
  const startedRef = useRef(false);
  const consumedRef = useRef(false);

  // Peek (don't consume yet) on mount so StrictMode's double-effect doesn't
  // race-lose the bridged context. We consume it only once analysis kicks off.
  useEffect(() => {
    const ctx = peekDeepModeContext();
    console.log('[DeepMode] mount — bridgeRequested:', bridgeRequested, 'peeked context:', !!ctx, ctx ? { origin: ctx.origin, msgs: ctx.messages?.length, handoffAt: ctx.handoffAt } : null);
    if (ctx) {
      setContext(ctx);
    } else if (bridgeRequested) {
      // Bridge URL was used but storage is empty — flag fallback UI.
      setContextMissing(true);
    }
  }, [bridgeRequested]);

  const inputText = useMemo(() => (context ? composeDeepInput(context.messages) : ''), [context]);
  const language = useMemo(() => (inputText ? detectLanguage(inputText) : (isZh ? 'zh' : 'en')) as 'zh' | 'en', [inputText, isZh]);

  const runAnalysis = useCallback(async () => {
    if (!inputText || inputText.trim().length < 10) {
      toast({
        title: T.dm_not_enough_title,
        description: T.dm_not_enough_desc,
        variant: 'destructive',
      });
      return;
    }
    setLoading(true);
    setPhase(1);
    setPhase1(null); setPhase2(null); setPhase3(null);
    try {
      const sanitized = inputText.trim().slice(0, 3000);

      const { data: p1, error: e1 } = await supabase.functions.invoke('consciousness-engine-demo', {
        body: { text: sanitized, language },
      });
      if (e1) throw e1;
      if ((p1 as any)?.error) throw new Error((p1 as any).error);
      if (p1.evidence) p1.evidence = p1.evidence.slice(0, 8);
      if (p1.phenomena) p1.phenomena = p1.phenomena.slice(0, 6);
      if (p1.edges) p1.edges = p1.edges.slice(0, 25);
      setPhase1(p1);
      setPhase(2);

      const { data: p2, error: e2 } = await supabase.functions.invoke('consciousness-engine-demo-phase2', {
        body: { text: sanitized, language, phase1_result: p1 },
      });
      if (e2) throw e2;
      if ((p2 as any)?.error) throw new Error((p2 as any).error);
      setPhase2(p2);
      setPhase(3);

      const { data: p3, error: e3 } = await supabase.functions.invoke('consciousness-engine-demo-phase3', {
        body: { text: sanitized, language, phase1_result: p1, phase2_result: p2 },
      });
      if (e3) throw e3;
      if ((p3 as any)?.error) throw new Error((p3 as any).error);
      setPhase3(p3);

      // Write a structured summary back so the chat can append it as an
      // assistant turn next time the user opens Reso.
      const topTherapies = (p3?.therapy_suggestions || []).slice(0, 3).map((t: any) => ({
        name_en: t.name_en || t.name || '',
        name_zh: t.name_zh || t.name_cn || '',
        reason: t.reason || t.rationale || '',
      }));
      const stage = p2?.healing_stage;
      const summary = isZh
        ? `📊 深入分析结果：${stage ? `当前阶段 — ${stage.stage_name}。` : ''}${topTherapies.length ? `推荐探索方向：${topTherapies.map((t: any) => t.name_zh || t.name_en).filter(Boolean).join('、')}。` : ''}`.trim()
        : `📊 Deep analysis complete. ${stage ? `Current focus — ${stage.stage_name}.` : ''}${topTherapies.length ? ` Suggested directions: ${topTherapies.map((t: any) => t.name_en).filter(Boolean).join(', ')}.` : ''}`.trim();
      setDeepModeResult({
        summary,
        healingStage: stage,
        structuralScores: p2?.structural_scores,
        topTherapies,
        completedAt: new Date().toISOString(),
      });
    } catch (err: any) {
      toast({
        title: T.dm_analysis_failed,
        description: err?.message || T.dm_try_later,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
      setPhase(0);
    }
  }, [inputText, language, isZh, toast, T]);

  // Auto-start when context arrives. Consume the bridged context exactly
  // once at the moment we actually start the analysis, not at mount —
  // this survives StrictMode double-effects and any lazy remount.
  useEffect(() => {
    if (!context || startedRef.current || loading || phase1) return;
    if (!inputText || inputText.length < 10) return;
    startedRef.current = true;
    if (!consumedRef.current) {
      consumedRef.current = true;
      consumeDeepModeContext();
    }
    console.log('[DeepMode] starting analysis — chars:', inputText.length, 'lang:', language, 'origin:', context.origin);
    runAnalysis();
  }, [context, inputText, loading, phase1, language, runAnalysis]);

  // Fallback: bridge was requested via ?deep=1 but the session context is
  // gone (expired, opened in a new tab, etc.). Render a clear empty state
  // with a path back to the conversation instead of a blank page.
  if (!context && !phase1 && !loading && (bridgeRequested || contextMissing)) {
    console.log('[DeepMode] render branch: fallback (bridge requested, no context)');
    return (
      <div className="rounded-2xl border border-amber-200 bg-white p-6 mb-8 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
            <AlertCircle className="h-5 w-5 text-amber-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg md:text-xl font-serif text-earth-900">
              {T.dm_expired_title}
            </h2>
            <p className="text-sm text-earth-700 mt-1">
              {T.dm_expired_desc}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button onClick={() => navigate('/')} className="bg-amber-600 hover:bg-amber-700 text-white">
                <MessageCircle className="h-4 w-4 mr-2" />
                {T.dm_back_to_chat}
              </Button>
              <Button variant="outline" onClick={() => navigate('/')}>
                {T.dm_back_to_home}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No bridged context and no bridge flag — Framework page renders normally
  // without the deep panel (it's a knowledge archive in that mode).
  if (!context && !phase1 && !loading) {
    console.log('[DeepMode] render branch: hidden (no bridge, no context)');
    return null;
  }


  const allEdges = [...(phase1?.edges || []), ...(phase2?.edges || [])];
  const hasAnyResult = phase1 || phase2 || phase3;
  console.log('[DeepMode] render branch: panel', { loading, phase, hasPhase1: !!phase1, hasPhase2: !!phase2, hasPhase3: !!phase3 });

  return (
    <div className="rounded-2xl border border-amber-200 bg-white p-4 md:p-6 mb-8 shadow-sm">
      {/* Header — frames this as continuation, not a new tool */}
      <div className="flex items-start gap-3 mb-4">
        <div className="shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
          <Sparkles className="h-5 w-5 text-amber-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-xl md:text-2xl font-serif text-earth-900">
            {T.dm_continue_title}
          </h2>
          <p className="text-xs md:text-sm text-earth-700 mt-1">
            {T.dm_continue_desc}
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/')}
          className="hidden md:inline-flex text-earth-700"
        >
          <MessageCircle className="h-4 w-4 mr-1" />
          {T.dm_back_short}
        </Button>
      </div>

      {/* Phase stepper */}
      {(loading || hasAnyResult) && (
        <div className="mb-4">
          <PhaseProgressStepper
            currentPhase={phase}
            phase1Done={!!phase1}
            phase2Done={!!phase2}
            phase3Done={!!phase3}
          />
        </div>
      )}

      {/* Initial loading */}
      {loading && phase === 1 && !phase1 && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-amber-600 mr-3" />
          <span className="text-sm text-earth-700">
            {T.dm_extracting}
          </span>
        </div>
      )}

      {/* Phase 1 */}
      {phase1 && (
        <div className="space-y-4 md:space-y-6">
          <div className="rounded-xl border border-border bg-card p-3 md:p-5">
            <Suspense
              fallback={
                <div className="flex items-center justify-center aspect-[2/3] md:aspect-square">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              }
            >
              <div className="aspect-[2/3] md:aspect-square">
                <ConsciousnessGraph3D
                  evidence={phase1.evidence}
                  phenomena={phase1.phenomena}
                  structuralScores={phase2?.structural_scores || {}}
                  falseV={
                    phase2?.false_v || {
                      smc: { level: '—', value: 0 },
                      msf: { level: '—', value: 0 },
                      ifr: { level: '—', value: 0 },
                      k: { level: '—', value: 0 },
                    }
                  }
                  edges={allEdges}
                />
              </div>
            </Suspense>
          </div>

          <div className="rounded-xl border border-border bg-card p-3 md:p-5">
            <EvidencePanel
              evidence={phase1.evidence}
              highlightedIds={highlightedEvIds}
              onEvidenceHover={(id) => setHighlightedEvIds(id ? [id] : [])}
            />
          </div>
          <div className="rounded-xl border border-border bg-card p-3 md:p-5">
            <PhenomenonPanel
              phenomena={phase1.phenomena}
              onPhenomenonHover={(ids) => setHighlightedEvIds(ids || [])}
            />
          </div>

          {loading && phase === 2 && (
            <div className="flex items-center gap-2 text-sm text-earth-700 px-2">
              <Loader2 className="h-4 w-4 animate-spin text-amber-600" />
              {T.dm_mapping}
            </div>
          )}

          {phase2 && (
            <>
              <div className="grid gap-4 md:gap-6 md:grid-cols-2">
                <div className="rounded-xl border border-border bg-card p-3 md:p-5">
                  <StructuralRadar scores={phase2.structural_scores} />
                </div>
                <div className="rounded-xl border border-border bg-card p-3 md:p-5">
                  <FalseVPanel falseV={phase2.false_v} />
                </div>
              </div>
              <div className="rounded-xl border border-border bg-card p-3 md:p-5">
                <HealingStagePanel healingStage={phase2.healing_stage} />
              </div>
            </>
          )}

          {phase2 && allEdges.length > 0 && (
            <div className="rounded-xl border border-border bg-card p-3 md:p-5">
              <CausalChainsPanel
                edges={allEdges}
                nodes={[
                  ...(phase1?.evidence?.map((e: any) => ({ id: e.evidence_id, label: e.source_text.slice(0, 40) })) || []),
                  ...(phase1?.phenomena?.map((p: any) => ({ id: p.id, label: p.display_name })) || []),
                ]}
              />
            </div>
          )}

          {loading && phase === 3 && (
            <div className="flex items-center gap-2 text-sm text-earth-700 px-2">
              <Loader2 className="h-4 w-4 animate-spin text-amber-600" />
              {T.dm_matching}
            </div>
          )}

          {phase3 && (
            <div className="rounded-xl border border-border bg-card p-3 md:p-5">
              <TherapySuggestions
                suggestions={phase3.therapy_suggestions}
                matchedTherapies={phase3.matched_therapies}
              />
            </div>
          )}

          {phase3?.matched_nutrients && phase3.matched_nutrients.length > 0 && (
            <div className="rounded-xl border border-border bg-card p-3 md:p-5">
              <NutrientPanel nutrients={phase3.matched_nutrients} />
            </div>
          )}

          {phase3?.ai_advice && phase3.ai_advice.length > 0 && (
            <div className="rounded-xl border border-border bg-card p-3 md:p-5">
              <AIAdvicePanel advice={phase3.ai_advice} />
            </div>
          )}

          {phase3 && (
            <div className="flex flex-wrap gap-2 pt-2 justify-center">
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="text-earth-700"
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                {T.dm_continue_reso}
              </Button>
              <Button
                onClick={() => navigate('/booking-choice')}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                {T.dm_see_practitioners}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DeepModePanel;
